/*  upload_trades.tsx
    – Bulk‑upload broker trade slices into Firestore
       Author: Yousef + ChatGPT helper
*/
import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import {
    Button,
    Card,
    NumberFormatter,
    Text,
    Grid,
    Alert,
    SimpleGrid,
    Select,
    Modal,
    Group,
    Table,
    ActionIcon,
    Loader,
    Tooltip,
    Divider,
    Badge,
} from '@mantine/core';
import {
    Timestamp,
    collection,
    getFirestore,
    DocumentData,
    getDocs,
    onSnapshot,
    query,
    where,
    writeBatch,
    QuerySnapshot,
    doc,
    updateDoc,
} from 'firebase/firestore';
import { IconAlertCircle, IconCheck, IconX, IconDownload, IconSend, IconTrash, IconAlertTriangle, IconCircle } from '@tabler/icons-react';
import { GenevaTradesRequest, BurganTrade, NotificationMSG, Security, Trade, TradeAccountConfig, TradeDateStats, TradeDate, PortfolioWarning, isAllfundsTradeAllocatable } from '../../types'; // Import the shared Trade type
import { DatePickerInput } from '@mantine/dates';
import { CSVLink } from 'react-csv';
import * as XLSX from 'xlsx';
import { parseDayFirst } from '../../helpers/common';
import { ConfigContext } from '../../contexts';
import { TradesTable } from '../trades/trades_table';
import { max } from 'underscore';
import { notifications } from '@mantine/notifications';
import { httpsCallable } from 'firebase/functions';
import { useFunctions } from '../../helpers/firebaseContext';
import BigNumber from 'bignumber.js';
import DownloadManualTradesTable from './download_manual_trades';
import { useNavigate } from 'react-router-dom';
import { fromMantineDate } from '../../helpers/mantineDates';
import PendingTradeDates from '../trades/pending_trade_dates';

export const headers = [
    { label: "Trx Date", key: "date" }, //tradeDate to be as date only e.g. 28/04/2025
    { label: "TrxTime", key: "datetime" }, //tradeDate to be as date and time e.g. 28/04/2025 9:19:05 AM
    { label: "Transaction Type", key: "type" }, //type to be 10 instead of Buy and 11 instead of Sell
    { label: "Security", key: "symbolCode" }, //the same
    { label: "Portfolio", key: "portfolioId" }, //the same
    { label: "MaturityDate", key: "empty" }, //always empty sell
    { label: "MarketMaker", key: "marketMaker" }, //always 0
    { label: "Contract Period", key: "contractPeriod" }, //always 0
    { label: "Market Trade No.", key: "id" }, // the same
    { label: "Broker", key: "broker" }, // fixed to 12
    { label: "StockCenter", key: "stockCenter" }, // to be translated based of KCC Account as there is mapping
    { label: "KCC Account", key: "kccAccount" }, // to be translated based of KCC Account as there is mapping
    { label: "Quantity", key: "filledQty" }, //the same
    { label: "Rate", key: "rate" }, // fixed to 1
    { label: "Price", key: "price" }, //the same
    { label: "Amount", key: "netSettle" }, //the same
    { label: "Brokerage", key: "commission" }, // to be taken from totalCommission but if it worth more than 50 kd substitute it with 0.5
    { label: "Stamps", key: "stamps" }, // to be 0.5 if the amount is more than 50 and 0 otherwise
    { label: "FirstFwdAmount", key: "firstFwdAmount" }, // always 0
    { label: "LastFwdAmount", key: "lastFwdAmount" }, // always 0
    { label: "KCC Ac. Ref No", key: "kccAcRefNo" }, // always 0
    { label: "Settlement Date", key: "date" }, //settlementDate to be as date only e.g. 28/04/2025
    { label: "Pending Quantity", key: "pendingQty" }, //the same
    { label: "TIF", key: "tif" },// the same
    { label: "CreatedDate", key: "date" }, //settelment date to be as date only e.g. 28/04/2025
    { label: "RequestSource", key: "requestSource" }, //for now always 'Phone'
];
/* ---------- main component ---------- */
const DownloadTrades = () => {
    // customer IDs to exclude from upload
    // const skipCustomerIds = ['11281385'];

    const [tradesDate, setTradesDate] = useState<Date | null>(null);
    const tradesDateRef = useRef<Date | null>(null);
    // const [msg, setMsg] = useState<NotificationMSG | null>(null);
    const [tradeDatesLoading, setTradeDatesLoading] = useState(false);
    const [selectedTradeDate, setSelectedTradeDate] = useState<TradeDate>();
    const [tradeDates, setTradeDates] = useState<Record<string, TradeDate>>({});
    const [dateAccounts, setDateAccounts] = useState<string[]>([]);
    const [symbolMap, setSymbolMap] = useState<Record<string, Security>>({});
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [deleteAccount, setDeleteAccount] = useState<string>('');
    const [actionLoadingStates, setActionLoadingStates] = useState<Record<string, { download?: boolean, geneva?: boolean, delete?: boolean }>>({});

    const [genevaLoading, setGenevaLoading] = useState<boolean>(false);

    const [accountWarnings, setAccountWarnings] = useState<Record<string, string[]>>({});
    const [syncStatusLoading, setSyncStatusLoading] = useState<boolean>(false);
    const [recalculateLoading, setRecalculateLoading] = useState<boolean>(false);

    const functions = useFunctions();

    const db = getFirestore();
    const navigate = useNavigate();

    const configContext = useContext(ConfigContext);

    // Fetch all securities once
    useEffect(() => {
        // Use onSnapshot instead of getDocs for real-time updates
        const unsubscribe = onSnapshot(
            query(collection(db, 'securities')),
            (snap) => {
                // Skip processing if data is from cache
                if (snap.metadata.fromCache) {
                    console.log('Ignoring cached data, waiting for server response');
                    return;
                }
                const map: Record<string, Security> = {};
                snap.forEach(doc => {
                    const data = doc.data();
                    map[doc.id] = data as Security;
                });

                setSymbolMap(map);
                console.log('Loaded securities count', snap.size);
            },
            (error) => {
                console.error('Error in securities snapshot:', error);
            }
        );

        // Clean up subscription on unmount
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        setTradeDatesLoading(true);

        // 1. Create a listener instead of getDocs
        const unsubscribe = onSnapshot(
            collection(db, "trade_dates"),
            (snapshot: QuerySnapshot<DocumentData>) => {
                try {
                    // reset loading (you could also do this after first snapshot)
                    setTradeDatesLoading(false);

                    // build raw arrays/records
                    const tradeDatesRawData: string[] = [];
                    let tradeDatesRecord: Record<string, TradeDate> = {};

                    snapshot.forEach((doc) => {
                        const id = doc.id;
                        const data = doc.data() as TradeDate;
                        tradeDatesRawData.push(id);
                        tradeDatesRecord[id] = data;
                    });

                    // parse dates and sort
                    const dates = tradeDatesRawData
                        .map((docID) => {
                            const [day, month, year] = docID.split("-").map(Number);
                            return new Date(year, month - 1, day);
                        })
                        .sort((a, b) => b.getTime() - a.getTime());

                    // Only set tradesDate if it's not already set or if the current date is no longer available
                    // Only pick a default when nothing is selected. A selected date may
                    // legitimately have no trade_dates doc (manual trades only live in
                    // the subcollection), so never snap it back to the latest stock date.
                    if (!tradesDateRef.current) {
                        const newDate = dates[0] ?? null;
                        setTradesDate(newDate);
                        tradesDateRef.current = newDate;
                    }

                    // if you need to refresh allocations:
                    tradeDatesRecord = refreshAllTradesAllocated(tradeDatesRecord);
                    setTradeDates(tradeDatesRecord);
                } catch (err) {
                    console.error("Error processing trade_dates snapshot:", err);
                }
            },
            (error) => {
                // listen error callback
                console.error("Realtime listener error:", error);
                setTradeDatesLoading(false);
            }
        );

        // 2. Cleanup listener on unmount
        return () => {
            unsubscribe();
        };
    }, [db]);

    // Sync ref when tradesDate changes (e.g., from date picker)
    useEffect(() => {
        tradesDateRef.current = tradesDate;
    }, [tradesDate]);

    useEffect(() => {
        if (tradesDate && tradeDates) {
            const d = tradeDates[formatDateToDocID(tradesDate)];
            if (d) {
                console.log('Selected trade date data:', d);
                setSelectedTradeDate(d);
                setDateAccounts(Object.keys(d.stats || {}));
            } else {
                // No trade_dates doc for this date (e.g. a future date with manual trades only)
                setSelectedTradeDate(undefined);
                setDateAccounts([]);
            }
        }
    }, [tradesDate, tradeDates]);

    // Function to handle warnings from TradesTable
    const handleTradesTableWarnings = useCallback((warnings: Record<string, PortfolioWarning[]>) => {
        // Convert portfolio warnings to account warnings format, preserving portfolio ID context
        const accountWarningsMap: Record<string, string[]> = {};

        console.log('🚨 Processing warnings from TradesTable:', warnings);

        Object.entries(warnings).forEach(([portfolioId, warningArray]) => {
            console.log(`📋 Portfolio ${portfolioId} has ${warningArray.length} warnings:`, warningArray);

            warningArray.forEach(warning => {
                // Include portfolio ID in the warning message for better context
                const warningWithPortfolio = `[${portfolioId}] ${warning.message}`;
                const accountId = warning.accountId;

                console.log(`⚠️  Mapping warning: Portfolio ${portfolioId} -> Account ${accountId}: ${warning.message}`);

                if (!accountWarningsMap[accountId]) {
                    accountWarningsMap[accountId] = [];
                }
                accountWarningsMap[accountId].push(warningWithPortfolio);
            });
        });

        console.log('📊 Final account warnings map:', accountWarningsMap);

        // Set the account warnings (replace, don't merge)
        setAccountWarnings(accountWarningsMap);
    }, []);

    // Accounts to render: stats accounts plus any account that only has warnings
    // (e.g. GLOBAL_DPM trades whose stats entry was never written) so warnings
    // are never silently dropped.
    const accountRows = useMemo(() => {
        return Array.from(new Set([...dateAccounts, ...Object.keys(accountWarnings)]));
    }, [dateAccounts, accountWarnings]);

    /*
    // Check for warnings in accounts
    useEffect(() => {
        const checkAccountWarnings = async () => {
            if (!tradesDate || !dateAccounts.length) {
                setAccountWarnings({});
                return;
            }

            const warnings: Record<string, string[]> = {};

            for (const accountId of dateAccounts) {
                const accountWarningsList = await getAccountWarnings(accountId);
                if (accountWarningsList.length > 0) {
                    warnings[accountId] = accountWarningsList;
                }
            }

            setAccountWarnings(warnings);
        };

        checkAccountWarnings();
    }, [tradesDate, dateAccounts, symbolMap]);

    const getAccountWarnings = async (accountId: string): Promise<string[]> => {
        if (!tradesDate) return [];

        try {
            const q = query(
                collection(db, "trade_dates", formatDateToDocID(tradesDate), "trades"),
                where("accountId", "==", accountId)
            );
            const snapshot = await getDocs(q);
            const trades: Trade[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as unknown as Trade));

            const warnings: string[] = [];

            for (const trade of trades) {
                // Check for missing allocations.  This is pointless though, removing it.
                // if (!trade.allocations || trade.allocations.length === 0) {
                //     warnings.push(`Trade ${trade.id}: Missing allocations`);
                // }

                // Check for incomplete allocations
                if (trade.allocations && trade.allocations.length > 0) {
                    const totalAllocatedQty = trade.allocations.reduce((sum, alloc) => new BigNumber(sum).plus(alloc.qty).toNumber(), 0);
                    if (new BigNumber(totalAllocatedQty).minus(trade.filledQty).abs().isGreaterThan(0.01)) {
                        warnings.push(`Trade ${trade.id}: Allocated quantity (${totalAllocatedQty}) doesn't match filled quantity (${trade.filledQty})`);
                    }
                }

                // Check for missing portfolio mappings
                if (trade.allocations) {
                    for (const allocation of trade.allocations) {
                        if (!allocation.portfolioId) {
                            warnings.push(`Trade ${trade.id}: Missing portfolio ID in allocation`);
                        }
                    }
                }

                // Check for missing security information
                if (!symbolMap[trade.symbolCode]) {
                    warnings.push(`Trade ${trade.id}: Unknown security ${trade.symbolCode}`);
                }
            }

            return warnings;
        } catch (error) {
            console.error(`Error checking warnings for account ${accountId}:`, error);
            return [`Error checking warnings for account ${accountId}`];
        }
    };
    */
    const refreshAllTradesAllocated = (
        tradeDates: Record<string, TradeDate>
    ): Record<string, TradeDate> => {
        if (!tradeDates) {
            return {};
        }
        const dates = Object.keys(tradeDates);

        for (const date of dates) {
            const statsMap = tradeDates[date]?.stats;
            if (statsMap) {
                const accounts = Object.keys(statsMap);
                for (const account of accounts) {
                    const stats = statsMap[account];
                    statsMap[account].allTradesAllocated = stats.allocatedTrades > 0 && stats.allocatedTrades === stats.totalRows;
                }
            }
        }

        return tradeDates;
    }

    const formatDateToDocID = (date?: Date): string => {
        if (!date) {
            return '-';
        }
        // export function formatDateToDocID(date: Date): string {
        const day = date.getDate();
        const month = date.getMonth() + 1; // zero-based
        const year = date.getFullYear();

        // pad to 2 digits
        const dd = String(day).padStart(2, "0");
        const mm = String(month).padStart(2, "0");

        return `${dd}-${mm}-${year}`;
    }
    function pad(n: number): string {
        return n < 10 ? `0${n}` : `${n}`;
    }
    function formatDateOnly(input: Timestamp | string): string {
        const date = input instanceof Timestamp ? input.toDate() : new Date(input);
        const day = pad(date.getDate());
        const month = pad(date.getMonth() + 1);
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }

    function formatDateTime(input: Timestamp | string): string {
        const date = input instanceof Timestamp ? input.toDate() : new Date(input);
        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());
        const seconds = pad(date.getSeconds());
        return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${hours}:${minutes}:${seconds}`;
    }
    const mapTradesToDocumentData = (tradesToMap: Trade[]): DocumentData[] => {
        const expandedTrades = expandTradesWithAllocations(tradesToMap);
        return expandedTrades.filter((trade) => trade.portfolioId).map(trade => {
            const config: TradeAccountConfig | undefined = configContext.tradesMap?.accounts ? configContext.tradesMap?.accounts[trade.accountId] || undefined : undefined;
            const stockCenter = config?.burgan_stockcenter_id ?? '';
            const kccAccount = config?.burgan_kcc_id ?? '';
            const brokerID = config?.burgan_broker_id ?? '12';
            if (!trade.currency) {
                trade.currency = getCurrencyFromRef(trade?.cashAcrRef);
            }
            const rate = trade.currency && selectedTradeDate?.fx_rates && selectedTradeDate?.fx_rates[trade.currency] ? selectedTradeDate?.fx_rates[trade.currency] : "1";

            const date = formatDateOnly(trade.tradeDate);
            const datetime = formatDateTime(trade.tradeDate);
            const type = trade.side === 'BUY' ? '10' : '11';
            // GCC exec IDs overflow a JS number (rounded execId repeats across
            // fills); the doc id suffix carries the exact digits.
            const id = Number.isSafeInteger(trade.execId) || !String(trade.id).startsWith(`${trade.accountId}_`)
                ? trade.execId
                : String(trade.id).slice(trade.accountId.length + 1);
            const orderValue = trade.orderValue;
            const currency = trade.currency || "KWD";

            const price = currency === "KWD" ? new BigNumber(trade.price).dividedBy(1000).toNumber() : trade.price; //convert to KD in currency is KWD
            const netSettle = currency === "KWD"
                ? trade.netSettle
                : new BigNumber(trade.filledQty).multipliedBy(price).toNumber();
            return {
                date,
                datetime,
                type,
                // AllFunds trades carry the ISIN in symbolCode — Burgan needs the security
                // doc's code (stamped on the trade as burganCode by the trigger).
                symbolCode: trade.source === 'allfunds' ? (trade.burganCode || trade.symbolCode) : trade.symbolCode,
                portfolioId: trade.portfolioId,
                empty: '',
                marketMaker: 0,
                contractPeriod: 0,
                id,
                broker: brokerID,
                stockCenter,
                kccAccount,
                filledQty: trade.filledQty,
                rate,
                price: price,
                orderValue,
                netSettle,
                commission: trade.commission,
                stamps: trade.stamps,
                firstFwdAmount: 0,
                lastFwdAmount: 0,
                kccAcRefNo: null,
                pendingQty: trade.pendingQty,
                tif: trade.tif,
                requestSource: 'Phone',
            };
        });
    };

    const expandTradesWithAllocations = (trades: Trade[]): BurganTrade[] => {
        return trades.flatMap(trade => {
            if (!trade.allocations || trade.allocations.length === 0) {
                // Skip trades with no allocations
                return [];
            }

            return trade.allocations.map(allocation => {
                const ratio = new BigNumber(allocation.qty).dividedBy(trade.filledQty);

                return {
                    ...trade,
                    portfolioId: allocation.portfolioId,
                    filledQty: allocation.qty,
                    orderQty: ratio.multipliedBy(trade.orderQty).toNumber(),
                    pendingQty: ratio.multipliedBy(trade.pendingQty).toNumber(),
                    orderValue: ratio.multipliedBy(trade.orderValue).toNumber(),
                    commission: allocation.commission,
                    stamps: allocation.stamp,
                    netSettle: ratio.multipliedBy(trade.netSettle).toNumber(),
                };
            });
        });
    }

    const getCurrencyFromRef = (
        cashAcrRef?: string,
    ): string => {
        if (!cashAcrRef || cashAcrRef.trim() === "") {
            return "KWD";
        }

        const match = cashAcrRef.match(/-([A-Z]{3})-/);
        return match ? match[1] : "KWD";
    };

    // Compute total quantities for table footer
    //   const totalOrderQty = Object.values(trades).reduce((sum, t) => sum + t.orderQty, 0);
    //   const totalFilledQty = Object.values(trades).reduce((sum, t) => sum + t.filledQty, 0);
    //   const totalPendingQty = Object.values(trades).reduce((sum, t) => sum + t.pendingQty, 0);

    // Helper functions for account-specific actions
    const handleDownloadForBurgan = async (accountId: string) => {
        if (!tradesDate) return;

        setActionLoadingStates(prev => ({ ...prev, [accountId]: { ...prev[accountId], download: true } }));

        try {
            // Fetch trades for this account
            const q = query(
                collection(db, "trade_dates", formatDateToDocID(tradesDate), "trades"),
                where("accountId", "==", accountId)
            );
            const snapshot = await getDocs(q);
            const trades: Trade[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Trade & { id: string }));

            // Generate Excel data
            const xlsData = mapTradesToDocumentData(trades);

            // Create worksheet data with headers
            const worksheetData = [
                headers.map(h => h.label), // Header row
                ...xlsData.map(row => headers.map(h => (row[h.key] !== undefined && row[h.key] !== null ? row[h.key] : '')))
            ];

            // Create workbook and worksheet
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Trades');

            // Generate Excel file and trigger download
            const xlsBuffer = XLSX.write(workbook, { bookType: 'xls', type: 'array' });
            const blob = new Blob([xlsBuffer], { type: 'application/vnd.ms-excel' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `trades_${accountId}_${formatDateToDocID(tradesDate)}.xls`;
            a.click();
            window.URL.revokeObjectURL(url);

            notifications.show({
                title: 'Success',
                message: 'Excel file downloaded successfully',
                icon: <IconCheck />,
                color: 'green',
            });
        } catch (error) {
            console.error("Error downloading CSV: ", error);
            notifications.show({
                title: 'Error',
                message: 'Failed to download CSV',
                icon: <IconX />,
                color: 'red',
            });
        } finally {
            setActionLoadingStates(prev => ({ ...prev, [accountId]: { ...prev[accountId], download: false } }));
        }
    };

    const handleSendToGeneva = async (accountId: string, action: "add" | "delete") => {
        if (!tradesDate) return;

        setActionLoadingStates(prev => ({ ...prev, [accountId]: { ...prev[accountId], geneva: true } }));

        try {
            // Fetch trades for this account
            const q = query(
                collection(db, "trade_dates", formatDateToDocID(tradesDate), "trades"),
                where("accountId", "==", accountId)
            );
            const snapshot = await getDocs(q);
            // "add" sends only the remaining (not yet LOCKED) trades so late arrivals on
            // an already-sent day can go out without resending the whole date; errored
            // (GENEVA_ERROR) trades are included, which is also their retry path.
            // In-flight AllFunds trades (no NAV/units yet) are never sendable.
            // "delete" (undo) only removes what is actually in Geneva (LOCKED).
            const docsToSend = snapshot.docs.filter(d => action === "add"
                ? d.data().state !== 'LOCKED' && isAllfundsTradeAllocatable(d.data() as Trade)
                : d.data().state === 'LOCKED');
            const tradePaths = docsToSend.map(doc => `trade_dates/${formatDateToDocID(tradesDate!)}/trades/${doc.id}`);

            if (tradePaths.length === 0) {
                notifications.show({
                    title: 'Nothing to send',
                    message: action === "add" ? 'All trades are already locked in Geneva' : 'No locked trades to remove',
                    color: 'yellow',
                });
                return;
            }

            const genevaTradesFn = httpsCallable<GenevaTradesRequest>(functions, 'genevaTrades');
            await genevaTradesFn({ trades: tradePaths, action: action });

            notifications.show({
                title: 'Success',
                message: `${tradePaths.length} trade${tradePaths.length > 1 ? 's' : ''} ${action === "add" ? 'sent to' : 'removed from'} Geneva`,
                icon: <IconCheck />,
                color: 'green',
            });
        } catch (error) {
            console.error("Error sending trades to Geneva: ", error);
            notifications.show({
                title: 'Error',
                message: 'Failed to send trades to Geneva: ' + error,
                icon: <IconX />,
                color: 'red',
            });
        } finally {
            setActionLoadingStates(prev => ({ ...prev, [accountId]: { ...prev[accountId], geneva: false } }));
        }
    };

    const handleDeleteTrades = async (accountId: string) => {
        if (!tradesDate) return;

        setDeleteAccount(accountId);
        setDeleteModal(true);
    };

    const handleSetNeedSync = async () => {
        if (!tradesDate) return;

        setSyncStatusLoading(true);

        try {
            const docRef = doc(db, "trade_dates", formatDateToDocID(tradesDate));
            await updateDoc(docRef, {
                syncStatus: "NEED_SYNC"
            });

            notifications.show({
                title: 'Success',
                message: 'Sync status updated to NEED_SYNC',
                icon: <IconCheck />,
                color: 'green',
            });
        } catch (error) {
            console.error("Error updating sync status: ", error);
            notifications.show({
                title: 'Error',
                message: 'Failed to update sync status',
                icon: <IconX />,
                color: 'red',
            });
        } finally {
            setSyncStatusLoading(false);
        }
    };

    const handleRecalculateStats = async () => {
        if (!tradesDate) return;

        setRecalculateLoading(true);

        try {
            const recalculateFn = httpsCallable(functions, 'recalculateTradeStatsForDate');
            const dateString = formatDateToDocID(tradesDate);

            await recalculateFn({ date: dateString });

            notifications.show({
                title: 'Success',
                message: 'Trade stats recalculated successfully',
                icon: <IconCheck />,
                color: 'green',
            });
        } catch (error) {
            console.error("Error recalculating trade stats: ", error);
            notifications.show({
                title: 'Error',
                message: 'Failed to recalculate trade stats: ' + error,
                icon: <IconX />,
                color: 'red',
            });
        } finally {
            setRecalculateLoading(false);
        }
    };

    /* ------------- UI ------------- */
    return (
        <>
            <Card shadow="sm" p="xl" radius="md" withBorder>
                <Grid gap="xs">
                    <Grid.Col span={3}>
                        <DatePickerInput
                            label="Trades Date"
                            firstDayOfWeek={0}
                            locale="en-gb"
                            placeholder="Select date"
                            valueFormat="DD/MM/YYYY"
                            value={tradesDate}
                            disabled={tradeDatesLoading}
                            onChange={(value) => {
                                if (value) {
                                    setTradesDate(fromMantineDate(value));
                                }
                            }}
                        />
                    </Grid.Col>
                </Grid>

                {selectedTradeDate && (
                    <div style={{ marginTop: 24, marginBottom: 8 }}>
                        <Group align="center" style={{ justifyContent: 'space-between' }}>
                            <div>
                                <Text size="sm" c="dimmed">Portfolio Sync Status</Text>
                                <Group gap="xs" mt={4}>
                                    <IconCircle
                                        size={12}
                                        fill={
                                            selectedTradeDate.syncStatus === 'DONE' ? 'green' :
                                                selectedTradeDate.syncStatus === 'ERROR' ? 'red' :
                                                    selectedTradeDate.syncStatus === 'SYNCING' ? 'orange' :
                                                        'gray'
                                        }
                                        color={
                                            selectedTradeDate.syncStatus === 'DONE' ? 'green' :
                                                selectedTradeDate.syncStatus === 'ERROR' ? 'red' :
                                                    selectedTradeDate.syncStatus === 'SYNCING' ? 'orange' :
                                                        'gray'
                                        }
                                    />
                                    <Text
                                        size="sm"
                                        fw={500}
                                        c={
                                            selectedTradeDate.syncStatus === 'DONE' ? 'green' :
                                                selectedTradeDate.syncStatus === 'ERROR' ? 'red' :
                                                    selectedTradeDate.syncStatus === 'SYNCING' ? 'orange' :
                                                        'gray'
                                        }
                                    >
                                        {selectedTradeDate.syncStatus?.replace(/_/g, ' ') || 'Unknown'}
                                    </Text>
                                </Group>
                            </div>
                            <Group gap="xs">
                                <Button
                                    size="xs"
                                    variant="light"
                                    color="orange"
                                    loading={syncStatusLoading}
                                    onClick={handleSetNeedSync}
                                >
                                    Set Need Sync
                                </Button>
                                <Button
                                    size="xs"
                                    variant="light"
                                    color="blue"
                                    loading={recalculateLoading}
                                    onClick={handleRecalculateStats}
                                >
                                    Recalculate Stats
                                </Button>
                                <Button
                                    size="xs"
                                    variant="light"
                                    color="green"
                                    onClick={() => {
                                        navigate("/trades/")
                                    }}
                                >
                                    Open Trades
                                </Button>
                            </Group>
                        </Group>
                    </div>
                )}

                <PendingTradeDates
                    tradeDates={tradeDates}
                    accounts={null}
                    showUnsent
                    loading={tradeDatesLoading}
                    selectedDateId={tradesDate ? formatDateToDocID(tradesDate) : undefined}
                    onSelectDate={setTradesDate}
                />

                {selectedTradeDate && accountRows.length > 0 && (
                    <Card withBorder p="md" radius="md">
                        <Text fw={500} mb="sm">Online Trades</Text>
                        <Table mt="md" striped highlightOnHover>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Account</Table.Th>
                                    <Table.Th>Allocated Trades</Table.Th>
                                    <Table.Th>Buy Total</Table.Th>
                                    <Table.Th>Sell Total</Table.Th>
                                    <Table.Th>Buy Value</Table.Th>
                                    <Table.Th>Sell Value</Table.Th>
                                    <Table.Th>Net Value</Table.Th>
                                    <Table.Th>Buy Qty</Table.Th>
                                    <Table.Th>Sell Qty</Table.Th>
                                    <Table.Th>States</Table.Th>
                                    <Table.Th>Sent</Table.Th>
                                    <Table.Th>Ready</Table.Th>
                                    <Table.Th>Warnings</Table.Th>
                                    <Table.Th>Actions</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {accountRows.map(accountId => {
                                    // Accounts surfaced only via warnings have no stats entry;
                                    // totalRows: 0 keeps every action safely disabled for them.
                                    const stats = selectedTradeDate.stats[accountId] ?? ({ totalRows: 0 } as TradeDateStats);
                                    const isReady = stats.allTradesAllocated;
                                    const loadingStates = actionLoadingStates[accountId] || {};

                                    // Calculate locked states for Geneva button logic
                                    const lockedCount = stats.states?.LOCKED ?? 0;
                                    const allTradesLocked = lockedCount === stats.totalRows && stats.totalRows > 0;
                                    const noLockedTrades = lockedCount === 0;
                                    // Partially sent: a late trade landed on a day already sent to Geneva
                                    const unsentCount = Math.max(0, (stats.totalRows ?? 0) - lockedCount);
                                    const partiallySent = lockedCount > 0 && unsentCount > 0;

                                    return (
                                        <Table.Tr key={accountId} bg={isReady ? 'green.0' : 'red.0'}>
                                            <Table.Td>
                                                <Group gap="xs">
                                                    <Text fw={500}>{accountId}</Text>
                                                    {/* {stats.inFlight && stats.inFlight > 0 && (
                                                    <Tooltip label={`${stats.inFlight} triggers still running`}>
                                                        <Loader size="xs" />
                                                    </Tooltip>
                                                )} */}
                                                </Group>
                                            </Table.Td>
                                            <Table.Td>
                                                <NumberFormatter
                                                    value={stats.allocatedTrades ?? 0}
                                                    thousandSeparator=","
                                                /> / <NumberFormatter
                                                    value={stats.totalRows ?? 0}
                                                    thousandSeparator=","
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                <NumberFormatter
                                                    value={stats.buyTotal ?? 0}
                                                    thousandSeparator=","
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                <NumberFormatter
                                                    value={stats.sellTotal ?? 0}
                                                    thousandSeparator=","
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                <NumberFormatter
                                                    value={stats.buyTotalValue ?? 0}
                                                    thousandSeparator=","
                                                    decimalScale={2}
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                <NumberFormatter
                                                    value={stats.sellTotalValue ?? 0}
                                                    thousandSeparator=","
                                                    decimalScale={2}
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                <NumberFormatter
                                                    value={stats.netTotalValue ?? 0}
                                                    thousandSeparator=","
                                                    decimalScale={2}
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                <NumberFormatter
                                                    value={stats.totalBuyQty ?? 0}
                                                    thousandSeparator=","
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                <NumberFormatter
                                                    value={stats.totalSellQty ?? 0}
                                                    thousandSeparator=","
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                {stats.states ? (
                                                    <Group gap="xs">
                                                        {Object.entries(stats.states).map(([state, count]) => (
                                                            count > 0 && (
                                                                <Text
                                                                    key={state}
                                                                    size="xs"
                                                                    c={state === 'LOCKED' ? 'orange' : 'red'}
                                                                    fw={500}
                                                                >
                                                                    {state}: {count}
                                                                </Text>
                                                            )
                                                        ))}
                                                        {Object.values(stats.states).every(count => count === 0) && (
                                                            <Text size="xs" c="green">-</Text>
                                                        )}
                                                    </Group>
                                                ) : (
                                                    <Text size="xs" c="dimmed">-</Text>
                                                )}
                                            </Table.Td>
                                            <Table.Td>
                                                {partiallySent ? (
                                                    <Tooltip label={`${unsentCount} trade${unsentCount > 1 ? 's' : ''} not yet sent to Geneva`}>
                                                        <Badge color="orange" variant="filled" size="sm">
                                                            {lockedCount}/{stats.totalRows}
                                                        </Badge>
                                                    </Tooltip>
                                                ) : allTradesLocked ? (
                                                    <Badge color="green" variant="light" size="sm">
                                                        {lockedCount}/{stats.totalRows}
                                                    </Badge>
                                                ) : (
                                                    <Text size="xs" c="dimmed">-</Text>
                                                )}
                                            </Table.Td>
                                            <Table.Td>
                                                <Text c={isReady ? 'green' : 'red'} fw={500}>
                                                    {isReady ? 'Yes' : 'No'}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>
                                                {accountWarnings[accountId] && accountWarnings[accountId].length > 0 ? (
                                                    <Tooltip
                                                        label={
                                                            <div style={{ maxWidth: 400 }}>
                                                                <Text size="sm" fw={500} mb="xs" c="orange">
                                                                    Account {accountId} - {accountWarnings[accountId].length} Warning{accountWarnings[accountId].length > 1 ? 's' : ''}:
                                                                </Text>
                                                                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                                                    {accountWarnings[accountId].map((warning, index) => (
                                                                        <Text key={index} size="xs" mb="xs" style={{ borderLeft: '2px solid orange', paddingLeft: 8 }}>
                                                                            {warning}
                                                                        </Text>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        }
                                                        withArrow
                                                        multiline
                                                        position="left"
                                                        openDelay={300}
                                                    >
                                                        <Group gap="xs">
                                                            <IconAlertTriangle size={16} color="orange" />
                                                            <Text size="xs" c="orange" fw={500}>
                                                                {accountWarnings[accountId].length}
                                                            </Text>
                                                        </Group>
                                                    </Tooltip>
                                                ) : (
                                                    <Text size="xs" c="green">-</Text>
                                                )}
                                            </Table.Td>
                                            <Table.Td>
                                                <Group gap="xs">
                                                    <ActionIcon
                                                        variant="light"
                                                        color="blue"
                                                        size="sm"
                                                        disabled={!isReady}
                                                        loading={loadingStates.download}
                                                        onClick={() => handleDownloadForBurgan(accountId)}
                                                    >
                                                        <IconDownload size={16} />
                                                    </ActionIcon>
                                                    <Tooltip label={partiallySent ? `Send remaining (${unsentCount}) to Geneva` : 'Send to Geneva'}>
                                                        <ActionIcon
                                                            variant="light"
                                                            color={partiallySent ? 'orange' : 'green'}
                                                            size="sm"
                                                            disabled={stats.totalRows === 0 || allTradesLocked}
                                                            loading={loadingStates.geneva}
                                                            onClick={() => handleSendToGeneva(accountId, "add")}
                                                        >
                                                            <IconSend size={16} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                    <ActionIcon
                                                        variant="light"
                                                        color="yellow"
                                                        size="sm"
                                                        disabled={stats.totalRows === 0 || noLockedTrades}
                                                        loading={loadingStates.geneva}
                                                        onClick={() => handleSendToGeneva(accountId, "delete")}
                                                    >
                                                        <IconX size={16} />
                                                    </ActionIcon>

                                                    <ActionIcon
                                                        variant="light"
                                                        color="red"
                                                        size="sm"
                                                        disabled={stats.totalRows === 0}
                                                        onClick={() => handleDeleteTrades(accountId)}
                                                    >
                                                        <IconTrash size={16} />
                                                    </ActionIcon>
                                                </Group>
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                            </Table.Tbody>
                        </Table>
                    </Card>

                )}


                {(!selectedTradeDate || accountRows.length === 0) && tradesDate && (
                    <Card withBorder p="md" radius="md">
                        <Text fw={500} mb="sm">Online Trades</Text>
                        <Text c="dimmed" ta="center" mt="md">
                            No accounts found for the selected date.
                        </Text>
                    </Card>
                )}
                <Divider my="md" />

                {tradesDate && <DownloadManualTradesTable
                    date={tradesDate}
                    onDateChange={setTradesDate}
                />}
            </Card>
            {/* hide the trades table and keep it just for the warnings.
                Must be a plain hidden div, NOT a closed Accordion.Panel: Mantine 9
                renders collapsed panels inside React <Activity mode="hidden">, which
                suspends all effects — the table would never subscribe or compute. */}
            <div style={{ display: 'none' }}>
                {selectedTradeDate && tradesDate &&
                    <TradesTable
                        preSelectedDate={tradesDate}
                        readonly={true}
                        showAllAccountsTrades={true}
                        expanded={false}
                        onWarningsChange={handleTradesTableWarnings}
                    />
                }
            </div>


            {/* Delete Confirmation Modal */}
            <Modal
                opened={deleteModal}
                onClose={() => setDeleteModal(false)}
                title="Confirm Deletion"
                size="sm"
            >
                <Text size="sm" mb="md">
                    Are you sure you want to delete all trades for date ({formatDateToDocID(tradesDate ?? undefined)}) and account ({deleteAccount})?
                </Text>
                <Text size="sm" c="orange" mb="md">
                    This action cannot be undone.
                </Text>
                <Group style={{ justifyContent: 'flex-end' }}>
                    <Button
                        variant="outline"
                        onClick={() => setDeleteModal(false)}
                        disabled={actionLoadingStates[deleteAccount]?.delete}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="red"
                        loading={actionLoadingStates[deleteAccount]?.delete}
                        onClick={async () => {
                            if (tradesDate && deleteAccount) {
                                setActionLoadingStates(prev => ({
                                    ...prev,
                                    [deleteAccount]: { ...prev[deleteAccount], delete: true }
                                }));

                                try {
                                    const q = query(
                                        collection(db, "trade_dates", formatDateToDocID(tradesDate), "trades"),
                                        where("accountId", "==", deleteAccount)
                                    );
                                    const snapshot = await getDocs(q);
                                    const batch = writeBatch(db);

                                    snapshot.docs.forEach(docSnap => {
                                        batch.delete(docSnap.ref);
                                    });

                                    await batch.commit();

                                    notifications.show({
                                        title: 'Delete complete',
                                        message: `${snapshot.docs.length} trades deleted`,
                                        icon: <IconCheck />,
                                        color: 'green',
                                    });
                                } catch (error) {
                                    console.error("Error deleting trades: ", error);
                                    notifications.show({
                                        title: 'Error',
                                        message: 'Error deleting trades',
                                        icon: <IconX />,
                                        color: 'red',
                                    });
                                } finally {
                                    setActionLoadingStates(prev => ({
                                        ...prev,
                                        [deleteAccount]: { ...prev[deleteAccount], delete: false }
                                    }));
                                    setDeleteModal(false);
                                }
                            }
                        }}
                    >
                        Delete
                    </Button>
                </Group>
            </Modal>
        </>
    );
};

export default DownloadTrades;
